/**
 * Gone quiet (top of Accounts): trials and paying customers who stopped
 * using SyncSuite (api lib/command/account-health.ts). Each one already has
 * a call task and sits on the Dialer when they have a phone; this is where
 * you see them all, call, or decide no call is needed.
 */
import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { BellOff, Headset, Moon, Settings2 } from "lucide-react";
import { api, ApiError } from "@/lib/api";
import { cn } from "@/lib/utils";
import { Button, Input, useToast } from "@/components/ui";
import { Card, dayLabel } from "@/features/command/ui";

interface Quiet {
  account_id: string;
  name: string;
  plan_code: string | null;
  kind: "quiet_trial" | "quiet_paying";
  last_seen: string;
  quiet_days: number;
  trial_ends_at: string | null;
  contact_id: string | null;
  contact_name: string | null;
  phone: string | null;
  at_risk: boolean;
  alert_id: string | null;
  task_id: string | null;
}
interface Settings { trial_quiet_days: number; paying_quiet_days: number }

export function QuietPanel() {
  const toast = useToast();
  const [rows, setRows] = useState<Quiet[] | null>(null);
  const [settings, setSettings] = useState<Settings | null>(null);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState<Settings>({ trial_quiet_days: 4, paying_quiet_days: 10 });

  const load = useCallback(async () => {
    try {
      const d = await api.get<{ quiet: Quiet[]; settings: Settings }>("/platform-accounts/quiet");
      setRows(d.quiet);
      setSettings(d.settings);
      setDraft({ trial_quiet_days: d.settings.trial_quiet_days, paying_quiet_days: d.settings.paying_quiet_days });
    } catch {
      setRows([]);
    }
  }, []);
  useEffect(() => { void load(); }, [load]);

  const dismiss = async (q: Quiet) => {
    if (!q.alert_id) return;
    try {
      await api.post(`/platform-accounts/quiet/${q.alert_id}/dismiss`);
      setRows((prev) => prev?.filter((x) => x.account_id !== q.account_id) ?? null);
      toast.success("Got it", `${q.contact_name ?? q.name} won't come up again unless they come back and go quiet again.`);
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Couldn't update that");
    }
  };

  const save = async () => {
    try {
      const d = await api.put<{ settings: Settings }>("/platform-accounts/quiet/settings", draft);
      setSettings(d.settings);
      setEditing(false);
      await load();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Couldn't save that");
    }
  };

  if (!rows || (!rows.length && !editing)) {
    return rows && settings ? (
      <p className="flex items-center gap-1.5 text-xs text-zinc-500">
        <Moon className="h-3.5 w-3.5" /> Nobody has gone quiet. Trials count as quiet after {settings.trial_quiet_days} days without using SyncSuite, paying customers after {settings.paying_quiet_days}.
        <button type="button" onClick={() => setEditing(true)} className="text-indigo-300 hover:text-indigo-200">Change</button>
      </p>
    ) : null;
  }

  return (
    <Card className="overflow-hidden">
      <div className="flex flex-wrap items-center justify-between gap-2 border-b border-zinc-800/70 px-4 py-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2 text-sm font-semibold text-zinc-100">
            <Moon className="h-4 w-4 text-amber-300" /> Gone quiet
            <span className="rounded-full bg-amber-500/15 px-2 py-0.5 text-[11px] font-medium text-amber-200">{rows.length}</span>
          </div>
          <p className="mt-0.5 text-xs text-zinc-500">
            Each one has a call task, and is on the Dialer when there's a phone number. It clears by itself when they come back or start paying.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Link to="/dialer" className="inline-flex h-8 items-center gap-1.5 rounded-lg bg-indigo-500 px-3 text-xs font-medium text-white hover:bg-indigo-400">
            <Headset className="h-3.5 w-3.5" /> Open the Dialer
          </Link>
          <button type="button" onClick={() => setEditing((v) => !v)} title="When someone counts as quiet" className="flex h-8 w-8 items-center justify-center rounded-lg text-zinc-400 hover:bg-zinc-800 hover:text-zinc-100">
            <Settings2 className="h-4 w-4" />
          </button>
        </div>
      </div>

      {editing && (
        <div className="flex flex-wrap items-end gap-3 border-b border-zinc-800/70 bg-zinc-900/40 px-4 py-3 text-sm">
          <label className="space-y-1">
            <span className="block text-xs text-zinc-400">Trials: quiet after (days)</span>
            <Input type="number" min={1} max={30} value={draft.trial_quiet_days} onChange={(e) => setDraft({ ...draft, trial_quiet_days: Number(e.target.value) })} className="w-24" />
          </label>
          <label className="space-y-1">
            <span className="block text-xs text-zinc-400">Paying: quiet after (days)</span>
            <Input type="number" min={1} max={90} value={draft.paying_quiet_days} onChange={(e) => setDraft({ ...draft, paying_quiet_days: Number(e.target.value) })} className="w-24" />
          </label>
          <Button size="sm" onClick={save}>Save</Button>
          <Button size="sm" variant="ghost" onClick={() => setEditing(false)}>Cancel</Button>
        </div>
      )}

      <ul className="divide-y divide-zinc-800/60">
        {rows.map((q) => (
          <li key={`${q.account_id}:${q.kind}`} className="flex flex-col gap-2 px-4 py-3 sm:flex-row sm:items-center">
            <Link to={`/accounts/${q.account_id}`} className="min-w-0 flex-1 hover:opacity-90">
              <span className="flex items-center gap-2">
                <span className={cn("h-1.5 w-1.5 shrink-0 rounded-full", q.at_risk ? "bg-rose-400" : "bg-amber-400")} />
                <span className="truncate text-sm font-medium text-zinc-100">{q.contact_name ?? q.name}</span>
                {q.contact_name && q.contact_name !== q.name && <span className="truncate text-xs text-zinc-500">{q.name}</span>}
              </span>
              <span className="mt-0.5 block pl-3.5 text-xs text-zinc-400">
                {q.kind === "quiet_trial" ? "Pro trial" : `Paying (${q.plan_code ?? "plan"})`}
                {" · "}last used SyncSuite {dayLabel(q.last_seen)}, {q.quiet_days} days ago
                {q.trial_ends_at && <span className={cn(q.at_risk && "text-rose-300")}>{" · "}charges {dayLabel(q.trial_ends_at)}</span>}
                {!q.phone && <span className="text-zinc-500">{" · "}no phone, so email them</span>}
              </span>
            </Link>
            <div className="flex shrink-0 items-center gap-1.5 pl-3.5 sm:pl-0">
              {q.contact_id && (
                <Link to={`/contacts/${q.contact_id}`} className="inline-flex h-8 items-center rounded-lg border border-zinc-700 px-2.5 text-xs font-medium text-zinc-200 hover:border-zinc-600 hover:bg-zinc-800/50">
                  {q.phone ? "Call or message" : "Message"}
                </Link>
              )}
              {q.alert_id && (
                <button type="button" onClick={() => void dismiss(q)} title="No call needed this time" className="inline-flex h-8 items-center gap-1 rounded-lg px-2 text-xs text-zinc-400 hover:bg-zinc-800 hover:text-zinc-100">
                  <BellOff className="h-3.5 w-3.5" /> No call needed
                </button>
              )}
            </div>
          </li>
        ))}
      </ul>
    </Card>
  );
}
